/* eslint-disable max-lines */
import {
  Autocomplete,
  Box,
  createFilterOptions,
  Grid,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { OccupationListEN, OccupationListES } from './OccupationList';
import LeadGenerationContext from '../../../context/LeadGenerationContext';
import { emplomentStyles as classes } from './EmploymentStyles';
import { ProductUIStyles } from '../StepTwo/ProductUIStyles';
import { CustomInput } from '../CustomInput/CustomInput';
import Reason from './Reason';
import AnnualIncome from './AnnualIncome';
import { commonUtility } from '../UtilityFunctions';
import {
  OCC_PLACEHOLDER,
  REQUIRED,
  PROFESSION_DETAILS,
  EMP_PLACEHOLDER,
  PREVIOUS_EMPLOYER,
} from '../../../constants';
import { autoCompleteOccupation, employedCommon, commonTernary, isBundle } from '../../commonCode';
import { CommonStyles } from '../CommonStyles';
import { productMapping } from '../../../services/productMapping';

function NotEmployed({ isBundleCompliance = false }) {
  const { t } = useTranslation();
  const [occupations, setOccupations] = useState([]);
  const leadGenerationContext = useContext(LeadGenerationContext);
  const { productName, theme } = leadGenerationContext;
  const commonStyles = CommonStyles(leadGenerationContext?.theme);
  const styledClasses = ProductUIStyles(leadGenerationContext?.theme);
  useEffect(() => {
    if (leadGenerationContext.language === 'ES') {
      setOccupations(OccupationListES());
    }
    if (leadGenerationContext.language === 'EN') {
      setOccupations(OccupationListEN());
    }
  }, [leadGenerationContext.language]);
  const setSourceOfCurrentIncome = (e) => {
    employedCommon(e, leadGenerationContext, '* Invalid Source of Current Income');
  };
  const setSchoolName = (e) => {
    employedCommon(e, leadGenerationContext, '* Invalid School Name');
  };
  const handleChange = (e) => {
    leadGenerationContext.setIsScreenFourDirty(true);
    leadGenerationContext.setIsScreenFiveDirty(true);
    leadGenerationContext.setIsFeatureScreenDirty(true);
    leadGenerationContext.setIsFundingScreenDirty(true);
    if (e.target.name === 'schoolName') {
      setSchoolName(e);
    } else if (e.target.name === 'previousEmployer') {
      employedCommon(e, leadGenerationContext, '* Invalid Previous Employer');
    } else if (e.target.name === 'sourceOfCurrentIncome') {
      setSourceOfCurrentIncome(e);
    } else if (e.target.value.length === 0) {
      leadGenerationContext.setEmploymentInformationErrors({
        ...leadGenerationContext.employmentInformationErrors,
        [e.target.name]: '* Mandatory Field',
      });
    } else {
      leadGenerationContext.setEmploymentInformationErrors({
        ...leadGenerationContext.employmentInformationErrors,
        [e.target.name]: '',
      });
    }
    if (e.target.name === 'reason') {
      const employmentInformation = {
        ...leadGenerationContext.employmentInformation,
        schoolName: '',
        sourceOfCurrentIncome: '',
        annualIncome: '',
        previousEmployer: '',
        occupation: '',
        reason: e.target.value,
      };
      const employmentInformationErrors = {
        ...leadGenerationContext.employmentInformationErrors,
        schoolName: '',
        sourceOfCurrentIncome: '',
        annualIncome: '',
        previousEmployer: '',
        occupation: '',
        reason: e.target.value.length === 0 ? '* Mandatory Field' : '',
      };
      leadGenerationContext.setEmploymentInformation(employmentInformation);
      leadGenerationContext.setEmploymentInformationErrors(employmentInformationErrors);
    } else {
      leadGenerationContext.setEmploymentInformation({
        ...leadGenerationContext.employmentInformation,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleAutoCompleteChangeNotEmp = (name, value) => {
    autoCompleteOccupation(name, value, leadGenerationContext);
  };

  const filterOptions = createFilterOptions({ matchFrom: 'start', stringify: (option) => option });

  const sortedOccupation = [...occupations].sort((a, b) => a.localeCompare(b));
  return (
    <>
      <Grid
        size={isBundleCompliance ? 12 : { xs: 12, md: 6 }}
        sx={commonTernary(
          !isBundleCompliance,
          classes.employment_select_field_padding,
          classes.bundle_employment_select_field
        )}
      >
        <Reason isBundleCompliance={isBundleCompliance} handleChange={handleChange} />
      </Grid>

      {leadGenerationContext.employmentInformation?.reason &&
      leadGenerationContext.employmentInformation.reason === 'Student' ? (
        <>
          <Grid
            size={isBundleCompliance ? 12 : { xs: 12, md: 6 }}
            sx={commonTernary(
              !isBundleCompliance,
              classes.employment_select_field_padding,
              classes.bundle_employment_select_field
            )}
          >
            <CustomInput
              id="schoolName"
              isBundle={isBundle(productName)}
              inputProps={{ maxLength: 40 }}
              placeholder={
                isBundleCompliance
                  ? t("Enter the institution's name", {
                      defaultValue: "Enter the institution's name",
                    })
                  : t('Enter School Name', {
                      defaultValue: 'Enter School Name',
                    })
              }
              fullWidth
              value={commonUtility(leadGenerationContext.employmentInformation?.schoolName)}
              error={commonUtility(leadGenerationContext.employmentInformationErrors?.schoolName)}
              InputClassName={classes.input_disabled_style}
              handleChange={handleChange}
              dataTestId="schoolname_testid"
              required={!isBundleCompliance}
              dataTestIdForLabel="schoolName_label"
              inputLabelAriaLabel={
                isBundleCompliance
                  ? `${t('Educational Institution', {
                      defaultValue: 'Educational Institution',
                    })} ${t(REQUIRED, { defaultValue: REQUIRED })}`
                  : `${t('School Name', { defaultValue: 'School Name' })} ${t(REQUIRED, {
                      defaultValue: REQUIRED,
                    })}`
              }
              label={
                isBundleCompliance ? (
                  <span
                    style={{
                      color: leadGenerationContext.employmentInformationErrors?.schoolName
                        ? 'red'
                        : 'inherit',
                    }}
                  >
                    {t('Educational Institution', {
                      defaultValue: 'Educational Institution',
                    })}
                  </span>
                ) : (
                  `${t('School Name', { defaultValue: 'School Name' })}`
                )
              }
              // isBundleCompliance={isBundleCompliance}
            />
            <Box
              component="span"
              sx={styledClasses.error_message_product}
              role="alert"
              data-testid="schoolErr_id"
            >
              {t(leadGenerationContext.employmentInformationErrors?.schoolName, {
                defaultValue: leadGenerationContext.employmentInformationErrors?.schoolName,
              })}
            </Box>
          </Grid>
          <Grid
            size={isBundleCompliance ? 12 : { xs: 12, md: 6 }}
            sx={commonTernary(
              !isBundleCompliance,
              classes.employment_select_field_padding,
              classes.bundle_employment_select_field
            )}
          >
            <CustomInput
              isBundle={isBundle(productName)}
              inputProps={{ maxLength: 100 }}
              placeholder={
                isBundleCompliance
                  ? t('Enter your source of income', {
                      defaultValue: 'Enter your source of income',
                    })
                  : t('Enter Source of Current Income', {
                      defaultValue: 'Enter Source of Current Income',
                    })
              }
              id="sourceOfCurrentIncome"
              value={commonUtility(
                leadGenerationContext.employmentInformation?.sourceOfCurrentIncome
              )}
              error={commonUtility(
                leadGenerationContext.employmentInformationErrors?.sourceOfCurrentIncome
              )}
              InputClassName={classes.input_disabled_style}
              handleChange={handleChange}
              fullWidth
              required={!isBundleCompliance}
              dataTestIdForLabel="sourceOfCurrentIncome_label"
              inputLabelAriaLabel={
                isBundleCompliance
                  ? `${t('Source of income', { defaultValue: 'Source of income' })} ${t(REQUIRED, {
                      defaultValue: REQUIRED,
                    })}`
                  : `${t('Source of Current Income', {
                      defaultValue: 'Source of Current Income',
                    })} ${t(REQUIRED, { defaultValue: REQUIRED })}`
              }
              label={
                isBundleCompliance ? (
                  <span
                    style={{
                      color: leadGenerationContext.employmentInformationErrors
                        ?.sourceOfCurrentIncome
                        ? 'red'
                        : 'inherit',
                    }}
                  >
                    {t('Source of income', { defaultValue: 'Source of income' })}
                  </span>
                ) : (
                  t('Source of Current Income', { defaultValue: 'Source of Current Income' })
                )
              }
              dataTestId="sourceOfIncome_testid"
              // isBundleCompliance={isBundleCompliance}
            />
            <Box
              component="span"
              sx={styledClasses.error_message_product}
              id="sincome_id"
              role="alert"
            >
              {t(leadGenerationContext.employmentInformationErrors?.sourceOfCurrentIncome, {
                defaultValue:
                  leadGenerationContext.employmentInformationErrors?.sourceOfCurrentIncome,
              })}
            </Box>
          </Grid>
        </>
      ) : (
        ''
      )}
      {leadGenerationContext.employmentInformation?.reason &&
      leadGenerationContext.employmentInformation.reason === 'No Previous Employer' ? (
        <>
          {isBundleCompliance && (
            <Grid
              size={isBundleCompliance ? 12 : { xs: 12, md: 6 }}
              sx={commonTernary(
                !isBundleCompliance,
                classes.employment_select_field_padding,
                classes.bundle_employment_select_field
              )}
            >
              <AnnualIncome handleChange={handleChange} isBundleCompliance={isBundleCompliance} />
            </Grid>
          )}
          <Grid
            size={isBundleCompliance ? 12 : { xs: 12, md: 6 }}
            sx={commonTernary(
              !isBundleCompliance,
              classes.employment_select_field_padding,
              classes.bundle_employment_select_field
            )}
          >
            <CustomInput
              isBundle={isBundle(productName)}
              inputProps={{ maxLength: 100 }}
              fullWidth
              id="sourceOfCurrentIncome"
              placeholder={
                isBundleCompliance
                  ? t('Enter your source of income', {
                      defaultValue: 'Enter your source of income',
                    })
                  : t('Enter Source of Current Income', {
                      defaultValue: 'Enter Source of Current Income',
                    })
              }
              handleChange={handleChange}
              value={commonUtility(
                leadGenerationContext.employmentInformation?.sourceOfCurrentIncome
              )}
              error={commonUtility(
                leadGenerationContext.employmentInformationErrors?.sourceOfCurrentIncome
              )}
              InputClassName={classes.input_disabled_style}
              dataTestId="sourceOfIncome_testid"
              inputLabelAriaLabel={
                isBundleCompliance
                  ? `${t('Source of income', {
                      defaultValue: 'Source of income',
                    })} ${t(REQUIRED, { defaultValue: REQUIRED })}`
                  : `${t('Source of Current Income', {
                      defaultValue: 'Source of Current Income',
                    })} ${t(REQUIRED, { defaultValue: REQUIRED })}`
              }
              dataTestIdForLabel="sourceOfCurrentIncome_label"
              label={
                isBundleCompliance ? (
                  <span
                    style={{
                      color: leadGenerationContext.employmentInformationErrors
                        ?.sourceOfCurrentIncome
                        ? 'red'
                        : 'inherit',
                    }}
                  >
                    {t('Source of income', { defaultValue: 'Source of income' })}
                  </span>
                ) : (
                  t('Source of Current Income', {
                    defaultValue: 'Source of Current Income',
                  })
                )
              }
              required={!isBundleCompliance}
              // isBundleCompliance={isBundleCompliance}
            />
            <Box
              component="span"
              sx={styledClasses.error_message_product}
              data-testid="sourceErr_id"
              role="alert"
            >
              {t(leadGenerationContext.employmentInformationErrors?.sourceOfCurrentIncome, {
                defaultValue:
                  leadGenerationContext.employmentInformationErrors?.sourceOfCurrentIncome,
              })}
            </Box>
          </Grid>
          {!isBundleCompliance && (
            <Grid
              size={isBundleCompliance ? 12 : { xs: 12, md: 6 }}
              sx={commonTernary(
                !isBundleCompliance,
                classes.employment_select_field_padding,
                classes.bundle_employment_select_field
              )}
            >
              <AnnualIncome handleChange={handleChange} isBundleCompliance={isBundleCompliance} />
            </Grid>
          )}
        </>
      ) : (
        ''
      )}
      {leadGenerationContext.employmentInformation?.reason &&
      leadGenerationContext.employmentInformation.reason === 'Not Currently Employed' ? (
        <>
          <Grid
            size={isBundleCompliance ? 12 : { xs: 12, md: 6 }}
            sx={commonTernary(
              !isBundleCompliance,
              classes.employment_select_field_padding,
              classes.bundle_employment_select_field
            )}
          >
            <CustomInput
              isBundle={isBundle(productName)}
              required={!isBundleCompliance}
              id="previousEmployer"
              inputProps={{
                maxLength: 40,
                style: { fontSize: '16px' },
              }}
              placeholder={
                isBundleCompliance
                  ? t(PREVIOUS_EMPLOYER, {
                      defaultValue: PREVIOUS_EMPLOYER,
                    })
                  : t(EMP_PLACEHOLDER, { defaultValue: EMP_PLACEHOLDER })
              }
              fullWidth
              value={commonUtility(leadGenerationContext.employmentInformation?.previousEmployer)}
              error={commonUtility(
                leadGenerationContext.employmentInformationErrors?.previousEmployer
              )}
              InputClassName={classes.input_disabled_style}
              handleChange={handleChange}
              dataTestId="previousEmployer_testid"
              dataTestIdForLabel="previousEmployer_label"
              inputLabelAriaLabel={`${t('Previous Employer', {
                defaultValue: 'Previous Employer',
              })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
              label={
                isBundleCompliance ? (
                  <span
                    style={{
                      color: leadGenerationContext.employmentInformationErrors?.previousEmployer
                        ? 'red'
                        : 'inherit',
                    }}
                  >
                    {t('Previous employer', { defaultValue: 'Previous employer' })}
                  </span>
                ) : (
                  t('Previous Employer', { defaultValue: 'Previous Employer' })
                )
              }
              // isBundleCompliance={isBundleCompliance}
            />
            <Box
              component="span"
              sx={styledClasses.error_message_product}
              role="alert"
              data-testid="prevEmpErr_id"
            >
              {t(leadGenerationContext.employmentInformationErrors?.previousEmployer, {
                defaultValue: leadGenerationContext.employmentInformationErrors?.previousEmployer,
              })}
            </Box>
          </Grid>
          <Grid
            size={isBundleCompliance ? 12 : { xs: 12, md: 6 }}
            sx={commonTernary(
              !isBundleCompliance,
              classes.employment_select_field_padding,
              classes.bundle_employment_select_field
            )}
          >
            <InputLabel
              htmlFor="occupation"
              required={!isBundleCompliance}
              id="occupation_label"
              data-testid="occupation_label"
              aria-label={
                isBundleCompliance
                  ? `${t('Profession', { defaultValue: 'Profession' })} ${t(REQUIRED, {
                      defaultValue: REQUIRED,
                    })}`
                  : `${t('Occupation', { defaultValue: 'Occupation' })} ${t(REQUIRED, {
                      defaultValue: REQUIRED,
                    })}`
              }
              sx={commonStyles.label_title}
            >
              {productMapping(leadGenerationContext.productName) === 'Elite Account' ||
              productMapping(leadGenerationContext.productName) === 'Libre' ||
              productMapping(leadGenerationContext.productName) === 'Ahorra' ? (
                t('OccupationProg', {
                  defaultValue: 'Occupation',
                  nsSeparator: '|',
                })
              ) : isBundleCompliance ? (
                <span
                  style={{
                    color: leadGenerationContext.employmentInformationErrors?.occupation
                      ? 'red'
                      : 'inherit',
                  }}
                >
                  {t('Profession', { defaultValue: 'Profession' })}
                </span>
              ) : (
                t('Occupation', { defaultValue: 'Occupation' })
              )}
            </InputLabel>
            <Autocomplete
              id="occupation"
              data-testid="autocomplete"
              name="occupation"
              options={sortedOccupation}
              getOptionLabel={(option) => option}
              value={commonUtility(leadGenerationContext.employmentInformation?.occupation)}
              onChange={(event, selectedValue) => {
                handleAutoCompleteChangeNotEmp('occupation', selectedValue);
              }}
              renderInput={(params) => (
                <OutlinedInput
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: '16px' },
                  }}
                  ref={params.InputProps.ref}
                  id="occupation"
                  name="occupation"
                  sx={[
                    commonStyles.outlined_inputs,
                    ...(isBundleCompliance
                      ? [
                          {
                            '&.Mui-focused': {
                              border: `1px solid ${theme?.palette?.primary?.main}`,
                            },
                          },
                          classes.select_inputs_bundleBorder,
                          commonStyles.outlined_input_bundle,
                          {
                            backgroundColor: leadGenerationContext.employmentInformationErrors
                              ?.occupation
                              ? '#D5151F1A'
                              : theme?.palette?.fieldsBackground?.main,
                          },
                        ]
                      : []),
                    {
                      padding: !isBundleCompliance && '11.5px 14px !important',
                      fontSize: '16px',
                    },
                  ]}
                  placeholder={
                    isBundleCompliance
                      ? t(PROFESSION_DETAILS, {
                          defaultValue: PROFESSION_DETAILS,
                        })
                      : t(OCC_PLACEHOLDER, {
                          defaultValue: OCC_PLACEHOLDER,
                        })
                  }
                  slotProps={{
                    notchedOutline: {
                      sx:
                        leadGenerationContext.employmentInformationErrors?.occupation &&
                        !isBundleCompliance
                          ? commonStyles.notchedOutlineErr
                          : leadGenerationContext.employmentInformationErrors?.occupation &&
                            isBundleCompliance
                          ? classes.outline_inputs_err_bundle
                          : commonStyles.notchedOutline,
                    },
                    input: {
                      sx: isBundle(productName) && {
                        padding: '14px 14px 10px 14px !important',
                        '::-moz-placeholder': {
                          color: `${theme?.palette?.fieldsPlaceholder?.main} !important`,
                          opacity: 1,
                        },
                        '::placeholder': {
                          color: `${theme?.palette?.fieldsPlaceholder?.main} !important`,
                          fontSize: '16px',
                          opacity: 1,
                        },
                        '::-webkit-input-placeholder': {
                          color: `${theme?.palette?.fieldsPlaceholder?.main} !important`,
                          opacity: 1,
                        },
                        '::-ms-input-placeholder': {
                          color: `${theme?.palette?.fieldsPlaceholder?.main} !important`,
                          opacity: 1,
                        },
                      },
                    },
                  }}
                  fullWidth
                  value={commonUtility(leadGenerationContext.employmentInformation?.occupation)}
                />
              )}
              filterOptions={filterOptions}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: theme?.menuBackground,
                    color: theme?.palette?.primary?.main,
                  },
                },
              }}
            />
            <Box component="span" sx={styledClasses.error_message_product} role="alert">
              {t(leadGenerationContext.employmentInformationErrors?.occupation, {
                defaultValue: leadGenerationContext.employmentInformationErrors?.occupation,
              })}
            </Box>
          </Grid>
          <Grid
            size={isBundleCompliance ? 12 : { xs: 12, md: 6 }}
            sx={commonTernary(
              !isBundleCompliance,
              classes.employment_select_field_padding,
              classes.bundle_employment_select_field
            )}
          >
            <AnnualIncome handleChange={handleChange} isBundleCompliance={isBundleCompliance} />
          </Grid>
          <Grid
            size={isBundleCompliance ? 12 : { xs: 12, md: 6 }}
            sx={commonTernary(
              !isBundleCompliance,
              classes.employment_select_field_padding,
              classes.bundle_employment_select_field
            )}
          >
            <CustomInput
              isBundle={isBundle(productName)}
              value={commonUtility(
                leadGenerationContext.employmentInformation?.sourceOfCurrentIncome
              )}
              error={commonUtility(
                leadGenerationContext.employmentInformationErrors?.sourceOfCurrentIncome
              )}
              id="sourceOfCurrentIncome"
              fullWidth
              inputProps={{ maxLength: 100 }}
              placeholder={
                isBundleCompliance
                  ? t('Enter your source of income', {
                      defaultValue: 'Enter your source of income',
                    })
                  : t('Enter Source of Current Income', {
                      defaultValue: 'Enter Source of Current Income',
                    })
              }
              required={!isBundleCompliance}
              InputClassName={classes.input_disabled_style}
              dataTestIdForLabel="sourceOfCurrentIncome_label"
              dataTestId="sourceOfIncome_testid"
              inputLabelAriaLabel={
                isBundleCompliance
                  ? `${t('Source of income', {
                      defaultValue: 'Source of income',
                    })} ${t(REQUIRED, { defaultValue: REQUIRED })}`
                  : `${t('Source of Current Income', {
                      defaultValue: 'Source of Current Income',
                    })} ${t(REQUIRED, { defaultValue: REQUIRED })}`
              }
              label={
                isBundleCompliance ? (
                  <span
                    style={{
                      color: leadGenerationContext.employmentInformationErrors
                        ?.sourceOfCurrentIncome
                        ? 'red'
                        : 'inherit',
                    }}
                  >
                    {t('Source of income', {
                      defaultValue: 'Source of income',
                    })}
                  </span>
                ) : (
                  t('Source of Current Income', {
                    defaultValue: 'Source of Current Income',
                  })
                )
              }
              handleChange={handleChange}
              // isBundleCompliance={isBundleCompliance}
            />
            <Box component="span" sx={styledClasses.error_message_product} role="alert">
              {t(leadGenerationContext.employmentInformationErrors?.sourceOfCurrentIncome, {
                defaultValue:
                  leadGenerationContext.employmentInformationErrors?.sourceOfCurrentIncome,
              })}
            </Box>
          </Grid>
        </>
      ) : (
        ''
      )}
    </>
  );
}

export default NotEmployed;

NotEmployed.propTypes = {
  isBundleCompliance: PropTypes.bool,
};
